import React, { useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Trans, useTranslation } from 'react-i18next';
import { Skeleton } from 'primereact/skeleton';
import { useQuery } from '@tanstack/react-query';
import { knowledgeBaseApi } from 'api/knowledgeBase';
import { useAuth } from 'contexts/authContext';
import { usePanel } from 'contexts/panelContext';
import { Step, StepsCommonData } from 'api';
import i18n from 'locale';
import { ProgressPairSteps, useProgressSteps } from 'hooks/useProgressSteps';
import { useProgramTranslation } from 'hooks/useProgramTranslation';
import { capitalizeWord } from 'utils/helpers';
import SubPageTitle from 'components/_common/panel/SubPageTitle';
import ProgressSteps, { ProgressStepItem } from './ProgressSteps';
import MenteePairQuickLinks from './MenteePairQuickLinks';
import MenteeProgressQuickLinks from './MenteeProgressQuickLinks';

type MenteeProgressStepsProps = {
	showProgress?: boolean;
};

const MenteeProgressSteps = ({ showProgress = true }: MenteeProgressStepsProps) => {
	const { t } = useTranslation();
	const { tProgram } = useProgramTranslation();
	const navigate = useNavigate();
	const { organizationName, programName, type } = useParams();
	const { currentUser } = useAuth();
	const { todosLoading, currentApplicationData, panelType } = usePanel();
	const { isLoading, isFetched, currentPairsSteps, commonSteps } = useProgressSteps();
	const menteeProgressTitleKey =
		panelType === 'mentor'
			? 'userPanel.progressSteps.mentee.titleForMentor'
			: 'userPanel.progressSteps.mentee.title';

	const stepItemMapper = useCallback(
		(step: Step): ProgressStepItem | null => {
			// Upcoming steps remain available as navigation shortcuts; their state is visual only.
			const disabled = false;
			const dataPairId = step.data?.pairId;
			const pairPath = `/panel/${organizationName}/${programName}/${type}/pair/${dataPairId}`;

			switch (step.type) {
				case 'process_summmary':
				case 'process_summary':
					return {
						id: `${step.type}-${step.no}`,
						label: t('userPanel.progressSteps.steps.process_summary'),
						href: `${pairPath}/process-summary`,
						disabled,
					};
				case 'after_last_session':
					return {
						id: `${step.type}-${step.no}`,
						label: t('userPanel.progressSteps.steps.after_last_session'),
						href: `${pairPath}/process-summary`,
						disabled,
					};
				case 'before_last_session':
					return {
						id: `${step.type}-${step.no}`,
						label: t('userPanel.progressSteps.steps.before_last_session'),
						href: `${pairPath}/session/last`,
						disabled,
					};
				case 'has_last_session':
					return {
						id: `${step.type}-${step.no}`,
						label: t('userPanel.progressSteps.steps.has_last_session'),
						href: `${pairPath}/session/last`,
						disabled,
					};
				case 'has_penultimate_session':
					return {
						id: `${step.type}-${step.no}`,
						label: t('userPanel.progressSteps.steps.has_penultimate_session'),
						href: `${pairPath}/session/penultimate`,
						disabled,
					};
				case 'after_mid_session':
					return {
						id: `${step.type}-${step.no}`,
						label: tProgram('userPanel.progressSteps.steps.after_mid_session'),
						href: `${pairPath}/session`,
						disabled,
					};
				case 'before_mid_session':
					return {
						id: `${step.type}-${step.no}`,
						label: t('userPanel.progressSteps.steps.before_mid_session'),
						href: `${pairPath}/session/middle`,
						disabled,
					};
				case 'after_second_session':
					return {
						id: `${step.type}-${step.no}`,
						label: tProgram('userPanel.progressSteps.steps.after_second_session'),
						href: `${pairPath}/session`,
						disabled,
					};
				case 'before_second_session':
					return {
						id: `${step.type}-${step.no}`,
						label: tProgram('userPanel.progressSteps.steps.before_second_session'),
						href: `/panel/${organizationName}/${programName}/${type}/pair/${dataPairId}/mentee-plan`,
						disabled,
					};
				case 'before_first_session':
					return {
						id: `${step.type}-${step.no}`,
						label: t('userPanel.progressSteps.steps.before_first_session'),
						href: `/panel/${organizationName}/${programName}/${type}/pair/${dataPairId}/contract`,
						disabled,
					};
				case 'has_first_session':
					return {
						id: `${step.type}-${step.no}`,
						label: t('userPanel.progressSteps.steps.has_first_session'),
						href: `${pairPath}/session/first`,
						disabled,
					};
				case 'has_entry_meeting':
					return {
						id: `${step.type}-${step.no}`,
						label: t('userPanel.progressSteps.steps.has_entry_meeting'),
						href: `/panel/${organizationName}/${programName}/${type}/pairing`,
						disabled,
					};
				case 'familiarize_with_program':
					return {
						id: `${step.type}-${step.no}`,
						label: t('userPanel.progressSteps.steps.familiarize_with_program'),
						href: `/${organizationName}/${programName}`,
						disabled,
					};
				case 'has_application_form':
					return {
						id: `${step.type}-${step.no}`,
						label: t('userPanel.progressSteps.steps.has_application_form'),
						href: `/${organizationName}/${programName}/apply-${type}`,
						disabled,
					};
				case 'choosing_mentor':
					return {
						id: `${step.type}-${step.no}`,
						label: t('userPanel.progressSteps.steps.choosing_mentor'),
						href: `/panel/${organizationName}/${programName}/${type}/pairing`,
						disabled,
					};
				case 'first_session':
					return {
						id: `${step.type}-${step.no}`,
						label: t('userPanel.progressSteps.steps.first_session'),
						href: `/panel/${organizationName}/${programName}/${type}/pairing`,
						disabled,
					};
				case 'fulfillment_of_contract':
					return {
						id: `${step.type}-${step.no}`,
						label: t('userPanel.progressSteps.steps.fulfillment_of_contract'),
						href: `/panel/${organizationName}/${programName}/${type}/pairing`,
						disabled,
					};
				case 'fulfillment_of_development_plan':
					return {
						id: `${step.type}-${step.no}`,
						label: (
							<Trans
								t={tProgram as any}
								i18nKey="userPanel.progressSteps.steps.fulfillment_of_development_plan"
								values={{
									type:
										i18n.resolvedLanguage === 'pl' && type === 'mentor'
											? 'Mentora'
											: capitalizeWord(type ?? ''),
								}}
							/>
						),
						href: `/panel/${organizationName}/${programName}/${type}/pairing`,
						disabled,
					};
				case 'next_mentoring_sessions':
					return {
						id: `${step.type}-${step.no}`,
						label: tProgram('userPanel.progressSteps.steps.next_mentoring_sessions'),
						href: `${pairPath}/session`,
						disabled,
					};
				case 'register_in_program':
					return {
						id: `${step.type}-${step.no}`,
						label: t('userPanel.progressSteps.steps.register_in_program'),
						href: `/${organizationName}/${programName}`,
						disabled,
					};
				default:
					return null;
			}
		},
		[organizationName, programName, t, tProgram, type]
	);

	const transformSteps = useCallback(
		({ steps }: Omit<StepsCommonData, 'steps'> & { steps: Step[] | null }) =>
			(steps || []).map((item) => stepItemMapper(item)).filter(Boolean) as ProgressStepItem[],
		[stepItemMapper]
	);

	const handleStepSelect = useCallback(
		(step: ProgressStepItem) => {
			if (step.href) {
				navigate({ pathname: step.href });
			}
		},
		[navigate]
	);

	const shouldFetchFaqArticle = Boolean(currentApplicationData?.id) && Boolean(commonSteps && !currentPairsSteps);

	const { data: articleData, isLoading: articleLoading } = useQuery(
		['KNOWLEDGE_BASE_ARTICLE_LIST_LABEL', { labels: ['faq'], language: currentUser?.language }],
		() => knowledgeBaseApi.application.getArticlesByLabelsOrIds(currentApplicationData.id, ['faq']),
		{
			enabled: shouldFetchFaqArticle,
			select: (data) => data[0] || null,
		}
	);

	const shouldShowArticleLoading = shouldFetchFaqArticle && articleLoading;

	if (isFetched && !commonSteps && !currentPairsSteps) {
		return null;
	}

	if (isLoading || todosLoading || shouldShowArticleLoading) {
		return (
			<div className="flex flex-column gap-2">
				<Skeleton className="inline-block h-2rem" width="40%" />
				<div className="w-full flex flex-row flex-wrap gap-2">
					<Skeleton className="flex-1-mhalf min90hpx" />
					<Skeleton className="flex-1-mhalf min90hpx" />
					<Skeleton className="flex-1-mhalf min90hpx" />
					<Skeleton className="flex-1-mhalf min90hpx" />
				</div>
			</div>
		);
	}

	return (
		<div className="flex flex-column gap-3">
			{commonSteps && !currentPairsSteps ? (
				<>
					<SubPageTitle title={tProgram('userPanel.progressSteps.welcome')} />
					<MenteeProgressQuickLinks faqArticle={articleData || null} />
					{showProgress ? (
						<ProgressSteps
							title={
								<Trans t={tProgram as any} i18nKey={menteeProgressTitleKey} components={[<strong />]} />
							}
							variant="mentee-white"
							steps={transformSteps(commonSteps)}
							activeStep={commonSteps.activeStep}
							onStepSelect={handleStepSelect}
						/>
					) : null}
				</>
			) : null}

			{currentPairsSteps && !commonSteps ? (
				<>
					{currentPairsSteps.length === 1 ? (
						<>
							<div className="py-3">
								<span className="text-base">
									{tProgram('userPanel.progressSteps.progressWith')}
									<span className="font-bold">{` ${currentPairsSteps[0].label || '-'}`}</span>
								</span>
							</div>
							<MenteePairQuickLinks pairSteps={currentPairsSteps[0]} />
							{showProgress && currentPairsSteps[0].steps !== null ? (
								<ProgressSteps
									title={
										<Trans
											t={tProgram as any}
											i18nKey={menteeProgressTitleKey}
											components={[<strong />]}
										/>
									}
									variant="mentee-white"
									steps={transformSteps(currentPairsSteps[0])}
									activeStep={currentPairsSteps[0].activeStep}
									onStepSelect={handleStepSelect}
								/>
							) : null}
						</>
					) : null}

					{currentPairsSteps.length >= 2 ? (
						<>
							<SubPageTitle title={tProgram('userPanel.progressSteps.yourMentoringPairs')} />
							<div className="flex flex-column gap-2">
								{currentPairsSteps.map((pairSteps: ProgressPairSteps) => (
									<MenteePairQuickLinks key={pairSteps.pairId} pairSteps={pairSteps} mini />
								))}
							</div>
						</>
					) : null}
				</>
			) : null}
		</div>
	);
};

export default MenteeProgressSteps;
